import { useState, useEffect, useMemo } from 'react'
import { Plus, Edit, Trash2, Save, X, Loader2, Folder, Tag, Search, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react'
import { api } from '../../../lib/api'

function CommitEvents({ lineId, line }) {
  const [events, setEvents] = useState([])
  const [categories, setCategories] = useState([])
  const [typesByCategory, setTypesByCategory] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({})
  const [saving, setSaving] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    if (lineId && line) {
      // Recargar categorías cuando cambie la línea (para obtener categorías filtradas por productType)
      loadCategories()
      loadCommitEvents()
    } else {
      setEvents([])
      setCategories([])
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId, line?.Product?.ProductTypeName, line?.ProductTypeName, line?.Product?.ProductType?.Name])

  useEffect(() => {
    if (selectedCategory) {
      console.log('🔄 [CommitEvents] useEffect: selectedCategory cambió a:', selectedCategory)
      loadTypesByCategory(selectedCategory)
    } else {
      // Limpiar tipos cuando no hay categoría seleccionada
      setTypesByCategory({})
      setSelectedType(null)
    }
  }, [selectedCategory, categories])

  // Precargar tipos para categorías presentes en los eventos (para mostrar nombres en la tabla)
  useEffect(() => {
    if (events.length > 0 && categories.length > 0) {
      const uniqueCategoryIds = new Set()
      events.forEach(event => {
        const categoryId = event.categoryId || event.CategoryID || event.EventCategory || event.eventCategory || event.Category || event.category
        if (categoryId) {
          uniqueCategoryIds.add(categoryId)
        }
      })
      
      // Precargar tipos para cada categoría única (sin esperar, en paralelo)
      const loadTypesPromises = Array.from(uniqueCategoryIds)
        .filter(categoryId => {
          // Verificar que la categoría existe en el array de categorías cargadas
          const categoryExists = categories.some(c => 
            (c.id || c.ID) === categoryId || 
            (c.name || c.Name) === categoryId ||
            String(c.id || c.ID) === String(categoryId) ||
            String(c.name || c.Name) === String(categoryId)
          )
          
          // Solo cargar si la categoría existe y no está ya cargada
          return categoryExists && (!typesByCategory[categoryId] || typesByCategory[categoryId].length === 0)
        })
        .map(categoryId => {
          return loadTypesByCategory(categoryId).catch(err => {
            console.warn(`⚠️ [CommitEvents] Error al precargar tipos para categoría ${categoryId}:`, err)
            return []
          })
        })
      
      // No esperar a que terminen todas las cargas, solo iniciarlas en paralelo
      if (loadTypesPromises.length > 0) {
        Promise.all(loadTypesPromises).catch(err => {
          console.warn('⚠️ [CommitEvents] Algunos tipos no se pudieron precargar:', err)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, categories])

  // Extraer Factory y ProdLine del objeto line
  const getFactoryAndProdLine = () => {
    if (!line) return { factory: '', prodLine: '' }
    
    // Factory puede venir de diferentes campos
    let factory = line.factory || line.Factory || ''
    
    // Si no hay factory, intentar extraerlo de hierarchicalLevel (array)
    if (!factory && line.hierarchicalLevel) {
      if (Array.isArray(line.hierarchicalLevel) && line.hierarchicalLevel.length > 0) {
        factory = line.hierarchicalLevel[0]
      } else if (typeof line.hierarchicalLevel === 'string') {
        // Si es string, puede estar en formato "Factory/Line" o similar
        const parts = line.hierarchicalLevel.split('/')
        if (parts.length > 0) {
          factory = parts[0].trim()
        }
      }
    }
    
    // ProdLine es el código de la línea
    const prodLine = line.code || line.Code || line.name || line.Name || ''
    
    // Log para debugging
    if (!factory && prodLine) {
      console.warn('⚠️ [CommitEvents] Factory no encontrado para línea:', {
        prodLine,
        hierarchicalLevel: line.hierarchicalLevel,
        line: line
      })
    }
    
    return { factory, prodLine }
  }

  // Obtener el productTypeName desde el objeto line
  const getProductTypeName = () => {
    if (!line) return null
    
    // Intentar obtener desde diferentes rutas posibles (orden de prioridad)
    const productTypeName = 
      line.ProductTypeName || // Primero verificar si ya está enriquecido directamente
      line.Product?.ProductTypeName ||
      line.Product?.ProductType?.Name ||
      line.productTypeName ||
      line.productType?.name ||
      line.ProductType?.Name ||
      line.mr_products?.ProductType?.Name ||
      line.mr_products?.ProductTypeName ||
      null
    
    // Solo loguear en modo debug si es necesario
    // No mostrar warning ya que es normal que algunos nodos (Factory, System) no tengan ProductTypeName
    if (productTypeName) {
      console.log('✅ [CommitEvents] ProductTypeName encontrado:', productTypeName)
    }
    // Removido el console.warn ya que es comportamiento esperado para nodos sin producto
    
    return productTypeName
  }

  const loadCategories = async () => {
    try {
      // Obtener productTypeName si está disponible
      const productTypeName = getProductTypeName()
      
      // Construir URL con query parameter si existe productTypeName
      let endpoint = '/api/events/categories'
      if (productTypeName) {
        endpoint += `?productTypeName=${encodeURIComponent(productTypeName)}`
        console.log('🔍 [CommitEvents] Cargando categorías con filtro productTypeName:', productTypeName, 'URL:', endpoint)
      } else {
        console.log('ℹ️ [CommitEvents] Cargando todas las categorías (sin filtro)')
      }
      
      // API: GET /events/categories?productTypeName=... (opcional)
      const response = await api.get(endpoint)
      console.log('✅ [CommitEvents] Categorías recibidas:', response?.length || 0, 'categorías')
      const categoriesData = Array.isArray(response) ? response : (response?.data || response?.categories || [])
      setCategories(categoriesData)
      
      if (categoriesData.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesData[0].id || categoriesData[0].ID || categoriesData[0].name || categoriesData[0].Name)
      }
    } catch (err) {
      console.error('Error loading categories:', err)
      // Si no hay categorías, usar array vacío
      setCategories([])
    }
  }

  const loadTypesByCategory = async (categoryId) => {
    if (!categoryId) return
    
    try {
      // Obtener el nombre de la categoría desde el array de categorías
      // Si categories está vacío, usar categoryId directamente como nombre
      let categoryName = categoryId
      if (categories.length > 0) {
        const category = categories.find(c => 
          (c.id || c.ID) === categoryId || 
          (c.name || c.Name) === categoryId ||
          String(c.id || c.ID) === String(categoryId) ||
          String(c.name || c.Name) === String(categoryId)
        )
        if (category) {
          categoryName = category.name || category.Name || categoryId
        }
      }
      
      if (!categoryName || categoryName.trim() === '') {
        console.warn('⚠️ [CommitEvents] Nombre de categoría vacío para categoryId:', categoryId)
        setTypesByCategory(prev => ({
          ...prev,
          [categoryId]: []
        }))
        return
      }
      
      // API: GET /events/typeByCat con header Category
      console.log('🔍 [CommitEvents] Cargando tipos para categoría:', categoryName)
      const response = await api.get('/api/events/typeByCat', {
        headers: {
          'Category': categoryName
        }
      })
      
      console.log('📦 [CommitEvents] Respuesta de /api/events/typeByCat:', response)
      
      // La API devuelve un objeto MsEventCategoryDTO con EventTypes
      // Estructura: { id, name, description, eventTypes: [...] }
      let typesData = []
      if (Array.isArray(response)) {
        typesData = response
      } else if (response?.eventTypes && Array.isArray(response.eventTypes)) {
        typesData = response.eventTypes
      } else if (response?.EventTypes && Array.isArray(response.EventTypes)) {
        typesData = response.EventTypes
      } else if (response?.data?.eventTypes && Array.isArray(response.data.eventTypes)) {
        typesData = response.data.eventTypes
      } else if (response?.data?.EventTypes && Array.isArray(response.data.EventTypes)) {
        typesData = response.data.EventTypes
      } else if (response?.types && Array.isArray(response.types)) {
        typesData = response.types
      } else if (response?.data && Array.isArray(response.data)) {
        typesData = response.data
      }
      
      console.log('✅ [CommitEvents] Tipos extraídos:', typesData.length, 'tipos')
      
      setTypesByCategory(prev => ({
        ...prev,
        [categoryId]: typesData
      }))
      
      if (typesData.length > 0 && !selectedType) {
        setSelectedType(typesData[0].id || typesData[0].ID || typesData[0].name || typesData[0].Name)
      }
    } catch (err) {
      console.error('Error loading types by category:', err)
      // Si el error es "category name cannot be empty", significa que la categoría no es válida
      if (err.message && err.message.includes('category name cannot be empty')) {
        console.log('ℹ️ [CommitEvents] Categoría inválida o vacía, usando array vacío')
        setTypesByCategory(prev => ({
          ...prev,
          [categoryId]: []
        }))
      }
    }
  }

  const loadCommitEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { factory, prodLine } = getFactoryAndProdLine()
      
      if (!factory || !prodLine) {
        console.warn('⚠️ [CommitEvents] Factory o ProdLine no disponibles:', { factory, prodLine, line })
        setEvents([])
        setLoading(false)
        return
      }
      
      // API: GET /api/events/commit con headers Factory y ProdLine
      const response = await api.get('/api/events/commit', {
        headers: {
          'Factory': factory,
          'ProdLine': prodLine
        }
      })
      
      const eventsData = Array.isArray(response) ? response : (response?.data || response?.events || [])
      setEvents(eventsData)
      setLoading(false)
    } catch (err) {
      // Si el error es 403 o "Registro no existe", significa que no hay eventos aún (no es un error crítico)
      // También verificar si está marcado como esperado
      if (err.isExpected || (err.message && (err.message.includes('Registro no existe') || err.message.includes('Listado no Mostrado') || err.status === 403))) {
        // Silenciar este error esperado - no hay eventos para esta línea
        setEvents([])
        setError(null) // No mostrar error si simplemente no hay datos
        setLoading(false)
        return
      }
      
      // Solo loguear errores reales (no esperados)
      console.error('Error loading commit events:', err)
      setError(err.message || 'Error loading commit events')
      setLoading(false)
    }
  }

  // Ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        }
      }
      return { key, direction: 'asc' }
    })
  }

  // Renderizar icono de ordenamiento
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="text-gray-400" />
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="text-blue-400" />
      : <ArrowDown size={14} className="text-blue-400" />
  }

  // Filtrar y ordenar eventos
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events

    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = events.filter(event => {
        const id = String(event.id || event.ID || '')
        const eventType = String(event.EventType || event.eventType || event.Type || '').toLowerCase()
        const eventCategory = String(event.EventCategory || event.eventCategory || event.Category || '').toLowerCase()
        const factory = String(event.Factory || event.factory || '').toLowerCase()
        const prodLine = String(event.ProdLine || event.prodLine || '').toLowerCase()
        const system = String(event.System || event.system || '').toLowerCase()
        const machine = String(event.Machine || event.machine || '').toLowerCase()
        const part = String(event.Part || event.part || '').toLowerCase()
        
        return id.includes(query) ||
               eventType.includes(query) ||
               eventCategory.includes(query) ||
               factory.includes(query) ||
               prodLine.includes(query) ||
               system.includes(query) ||
               machine.includes(query) ||
               part.includes(query)
      })
    }

    // Ordenar por fecha descendente por defecto (más reciente primero)
    // Si hay un sortConfig específico, usar ese, sino ordenar por EventTime DESC
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aVal = a[sortConfig.key] || ''
        let bVal = b[sortConfig.key] || ''

        // Manejar diferentes formatos de campos
        if (sortConfig.key === 'id' || sortConfig.key === 'ID') {
          aVal = a.id || a.ID || 0
          bVal = b.id || b.ID || 0
        } else if (sortConfig.key === 'EventTime' || sortConfig.key === 'eventTime' || sortConfig.key === 'timestamp') {
          aVal = new Date(a.EventTime || a.eventTime || a.timestamp || a.Timestamp || 0).getTime()
          bVal = new Date(b.EventTime || b.eventTime || b.timestamp || b.Timestamp || 0).getTime()
        } else {
          aVal = String(aVal).toLowerCase()
          bVal = String(bVal).toLowerCase()
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    } else {
      // Ordenar por fecha descendente por defecto (más reciente primero)
      filtered = [...filtered].sort((a, b) => {
        const aTime = new Date(a.EventTime || a.eventTime || a.timestamp || a.Timestamp || 0).getTime()
        const bTime = new Date(b.EventTime || b.eventTime || b.timestamp || b.Timestamp || 0).getTime()
        return bTime - aTime // Descendente (más reciente primero)
      })
    }

    return filtered
  }, [events, searchQuery, sortConfig])

  // Paginación
  const totalPages = Math.ceil(filteredAndSortedEvents.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedEvents = filteredAndSortedEvents.slice(startIndex, endIndex)

  // Resetear página cuando cambie la búsqueda, filtros o línea
  useEffect(() => {
    setCurrentPage(1)
  }, [lineId, searchQuery])

  // Ajustar página si está fuera de rango
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
      // Scroll al inicio de la tabla
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Agrupar eventos por categoría y tipo (para el formato anterior, si se necesita)
  const groupedEvents = useMemo(() => {
    const grouped = {}
    
    events.forEach(event => {
      const categoryId = event.categoryId || event.CategoryID || event.EventCategory || event.eventCategory || event.category?.id || event.category?.ID || 'uncategorized'
      const typeId = event.typeId || event.TypeID || event.EventType || event.eventType || event.type?.id || event.type?.ID || 'untyped'
      
      if (!grouped[categoryId]) {
        grouped[categoryId] = {}
      }
      if (!grouped[categoryId][typeId]) {
        grouped[categoryId][typeId] = []
      }
      
      grouped[categoryId][typeId].push(event)
    })
    
    return grouped
  }, [events])

  const handleEdit = (event) => {
    setEditingId(event.id || event.ID)
    
    // Obtener timestamp del evento o usar fecha/hora actual si no existe
    let eventTimestamp = event.timestamp || event.Timestamp || event.EventTime || event.eventTime || ''
    if (!eventTimestamp) {
      eventTimestamp = new Date().toISOString()
    }
    
    setFormData({
      id: event.id || event.ID,
      lineId: event.lineId || event.LineID || lineId,
      categoryId: event.categoryId || event.CategoryID || event.category?.id || event.category?.ID || '',
      typeId: event.typeId || event.TypeID || event.type?.id || event.type?.ID || '',
      timestamp: eventTimestamp,
      ...event
    })
    setSelectedCategory(event.categoryId || event.CategoryID || event.category?.id || event.category?.ID || '')
    setSelectedType(event.typeId || event.TypeID || event.type?.id || event.type?.ID || '')
  }

  const handleCancel = () => {
    setEditingId(null)
    setFormData({})
    setSelectedCategory(null)
    setSelectedType(null)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      const { factory, prodLine } = getFactoryAndProdLine()
      
      if (!factory || !prodLine) {
        setError('Factory o ProdLine no disponibles')
        setSaving(false)
        return
      }
      
      // Obtener nombres de categoría y tipo
      const category = categories.find(c => 
        (c.id || c.ID) === selectedCategory || 
        (c.name || c.Name) === selectedCategory
      )
      const categoryName = category ? (category.name || category.Name || selectedCategory) : selectedCategory
      
      const types = typesByCategory[selectedCategory] || []
      const type = types.find(t => 
        (t.id || t.ID) === selectedType || 
        (t.name || t.Name) === selectedType
      )
      const typeName = type ? (type.name || type.Name || selectedType) : selectedType
      
      // Usar timestamp del formData o fecha/hora actual si no existe o es inválido
      let eventTime = formData.timestamp || formData.eventTime || ''
      if (!eventTime) {
        eventTime = new Date().toISOString()
      } else {
        // Validar que la fecha sea válida
        try {
          const date = new Date(eventTime)
          if (isNaN(date.getTime())) {
            eventTime = new Date().toISOString()
          } else {
            eventTime = date.toISOString()
          }
        } catch {
          eventTime = new Date().toISOString()
        }
      }
      
      const system = formData.system || formData.System || ''
      const machine = formData.machine || formData.Machine || ''
      const part = formData.part || formData.Part || ''
      
      const headers = {
        'EventTime': eventTime,
        'Factory': factory,
        'ProdLine': prodLine,
        'System': system,
        'Machine': machine,
        'Part': part,
        'Type': typeName,
        'Category': categoryName
      }
      
      if (editingId) {
        // Actualizar evento existente
        // API: GET /api/events/commit/update con headers
        headers['ID'] = String(editingId)
        await api.get('/api/events/commit/update', { headers })
      } else {
        // Crear nuevo evento
        // API: GET /api/events/commit/add con headers
        await api.get('/api/events/commit/add', { headers })
      }
      
      setEditingId(null)
      setFormData({})
      await loadCommitEvents()
      setSaving(false)
    } catch (err) {
      console.error('Error saving commit event:', err)
      setError(err.message || 'Error saving event')
      setSaving(false)
    }
  }

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return
    }
    
    try {
      // API: GET /api/events/commit/del con header ID
      await api.get('/api/events/commit/del', {
        headers: {
          'ID': String(eventId)
        }
      })
      await loadCommitEvents()
    } catch (err) {
      console.error('Error deleting commit event:', err)
      setError(err.message || 'Error deleting event')
    }
  }

  const handleAddNew = () => {
    setEditingId(null)
    setFormData({
      lineId: lineId,
      categoryId: '',
      typeId: '',
      timestamp: new Date().toISOString()
    })
    
    if (categories.length > 0) {
      setSelectedCategory(categories[0].id || categories[0].ID || categories[0].name || categories[0].Name)
    }
  }

  const getCategoryName = (categoryIdOrName) => {
    if (!categoryIdOrName) return 'N/A'
    
    // Buscar la categoría por ID o nombre (comparación flexible)
    const category = categories.find(c => 
      (c.id || c.ID) === categoryIdOrName || 
      (c.name || c.Name) === categoryIdOrName ||
      String(c.id || c.ID) === String(categoryIdOrName) ||
      String(c.name || c.Name) === String(categoryIdOrName) ||
      String(c.name || c.Name).toLowerCase() === String(categoryIdOrName).toLowerCase()
    )
    
    // Si se encuentra la categoría, retornar su nombre
    if (category) {
      return category.name || category.Name || categoryIdOrName
    }
    
    // Si no se encuentra, puede ser que ya sea un nombre o un ID que no está en la lista
    // Retornar el valor tal cual (puede ser un nombre que aún no está cargado)
    return categoryIdOrName
  }

  const getTypeName = (categoryId, typeIdOrName) => {
    if (!typeIdOrName) return 'N/A'
    
    // Si no hay categoryId, intentar buscar en todas las categorías cargadas
    if (!categoryId) {
      // Buscar en todas las categorías cargadas
      for (const catId in typesByCategory) {
        const types = typesByCategory[catId] || []
        const type = types.find(t => 
          (t.id || t.ID) === typeIdOrName || 
          (t.name || t.Name) === typeIdOrName ||
          String(t.id || t.ID) === String(typeIdOrName) ||
          String(t.name || t.Name) === String(typeIdOrName) ||
          String(t.name || t.Name).toLowerCase() === String(typeIdOrName).toLowerCase()
        )
        if (type) {
          return type.name || type.Name || typeIdOrName
        }
      }
      // Si no se encuentra en ninguna categoría cargada, retornar el valor tal cual
      return typeIdOrName
    }
    
    // Buscar el tipo en la categoría específica
    const types = typesByCategory[categoryId] || []
    const type = types.find(t => 
      (t.id || t.ID) === typeIdOrName || 
      (t.name || t.Name) === typeIdOrName ||
      String(t.id || t.ID) === String(typeIdOrName) ||
      String(t.name || t.Name) === String(typeIdOrName) ||
      String(t.name || t.Name).toLowerCase() === String(typeIdOrName).toLowerCase()
    )
    
    // Si se encuentra el tipo, retornar su nombre
    if (type) {
      return type.name || type.Name || typeIdOrName
    }
    
    // Si no se encuentra, puede ser que ya sea un nombre o un ID que no está cargado
    // Retornar el valor tal cual (puede ser un nombre que aún no está cargado)
    return typeIdOrName
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-red-500 p-4 bg-red-900/20 rounded-lg">
        Error: {error}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Commit Events</h3>
          <p className="text-sm text-gray-400">
            {filteredAndSortedEvents.length} {filteredAndSortedEvents.length === 1 ? 'evento' : 'eventos'}
            {searchQuery && ` (filtrados de ${events.length})`}
            {totalPages > 1 && ` - Página ${currentPage} de ${totalPages}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => loadCommitEvents()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
            title="Refrescar"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {!editingId && (
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus size={16} />
              Add Event
            </button>
          )}
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Buscar por ID, Tipo, Categoría, Factory, Línea, Sistema, Máquina, Parte..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Formulario de edición/creación */}
      {(!editingId && Object.keys(formData).length > 0) || editingId ? (
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Category
              </label>
              <select
                value={selectedCategory || ''}
                onChange={(e) => {
                  setSelectedCategory(e.target.value)
                  setSelectedType(null)
                  setTypesByCategory({})
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category</option>
                {categories.map((cat) => {
                  const catId = cat.id || cat.ID || cat.name || cat.Name
                  const catName = cat.name || cat.Name || catId
                  return (
                    <option key={catId} value={catId}>
                      {catName}
                    </option>
                  )
                })}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Type
              </label>
              <select
                value={selectedType || ''}
                onChange={(e) => {
                  console.log('🔄 [CommitEvents] Tipo seleccionado:', e.target.value)
                  setSelectedType(e.target.value)
                }}
                disabled={!selectedCategory || (typesByCategory[selectedCategory] || []).length === 0}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">
                  {!selectedCategory 
                    ? 'Selecciona una categoría primero' 
                    : (typesByCategory[selectedCategory] || []).length === 0 
                      ? 'Cargando tipos...' 
                      : 'Select type'}
                </option>
                {(typesByCategory[selectedCategory] || []).map((type) => {
                  const typeId = type.id || type.ID || type.name || type.Name
                  const typeName = type.name || type.Name || typeId
                  return (
                    <option key={typeId} value={typeId}>
                      {typeName}
                    </option>
                  )
                })}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Timestamp
              </label>
              <input
                type="datetime-local"
                value={formData.timestamp 
                  ? (() => {
                      try {
                        const date = new Date(formData.timestamp)
                        if (isNaN(date.getTime())) {
                          // Si la fecha es inválida, usar fecha actual
                          return new Date().toISOString().slice(0, 16)
                        }
                        return date.toISOString().slice(0, 16)
                      } catch {
                        // Si hay error al parsear, usar fecha actual
                        return new Date().toISOString().slice(0, 16)
                      }
                    })()
                  : new Date().toISOString().slice(0, 16) // Por defecto: fecha/hora actual
                }
                onChange={(e) => {
                  // Convertir el valor datetime-local a ISO string
                  const isoValue = e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString()
                  setFormData({ ...formData, timestamp: isoValue })
                }}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSave}
              disabled={saving || !selectedCategory || !selectedType}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <Save size={16} />
              )}
              Save
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {/* Tabla de eventos */}
      {filteredAndSortedEvents.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-2">
            {searchQuery ? 'No se encontraron eventos que coincidan con la búsqueda' : 'No hay eventos commit para esta línea'}
          </p>
          <p className="text-sm text-gray-500">
            {searchQuery ? 'Intenta con otros términos de búsqueda' : 'Crea un nuevo evento usando el botón "Add Event"'}
          </p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center gap-2">
                      ID
                      <SortIcon columnKey="id" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('EventTime')}
                  >
                    <div className="flex items-center gap-2">
                      Fecha/Hora
                      <SortIcon columnKey="EventTime" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('EventCategory')}
                  >
                    <div className="flex items-center gap-2">
                      Categoría
                      <SortIcon columnKey="EventCategory" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('EventType')}
                  >
                    <div className="flex items-center gap-2">
                      Tipo
                      <SortIcon columnKey="EventType" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('Factory')}
                  >
                    <div className="flex items-center gap-2">
                      Factory
                      <SortIcon columnKey="Factory" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-800 transition-colors"
                    onClick={() => handleSort('ProdLine')}
                  >
                    <div className="flex items-center gap-2">
                      Línea
                      <SortIcon columnKey="ProdLine" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Sistema
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Máquina
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Parte
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paginatedEvents.map((event) => {
                  const eventId = event.id || event.ID
                  const isEditing = editingId === eventId
                  
                  return (
                    <tr
                      key={eventId}
                      className={`hover:bg-gray-700/50 transition-colors ${isEditing ? 'bg-blue-900/20' : ''}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-white">{eventId}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">
                          {event.EventTime || event.eventTime || event.timestamp || event.Timestamp
                            ? new Date(event.EventTime || event.eventTime || event.timestamp || event.Timestamp).toLocaleString('es-ES', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">
                          {(() => {
                            // Obtener el ID de categoría del evento (puede venir como ID o nombre)
                            const categoryId = event.categoryId || event.CategoryID || event.EventCategory || event.eventCategory || event.Category || event.category
                            // Si categoryId existe, usar getCategoryName para obtener el nombre
                            if (categoryId) {
                              return getCategoryName(categoryId)
                            }
                            return 'N/A'
                          })()}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-semibold text-blue-400">
                          {(() => {
                            // Obtener el ID de categoría del evento (necesario para buscar el tipo)
                            const categoryId = event.categoryId || event.CategoryID || event.EventCategory || event.eventCategory || event.Category || event.category
                            // Obtener el ID de tipo del evento (puede venir como ID o nombre)
                            const typeId = event.typeId || event.TypeID || event.EventType || event.eventType || event.Type || event.type
                            // Si typeId existe, usar getTypeName para obtener el nombre
                            if (typeId) {
                              return getTypeName(categoryId, typeId)
                            }
                            return 'N/A'
                          })()}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.Factory || event.factory || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.ProdLine || event.prodLine || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.System || event.system || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.Machine || event.machine || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm text-gray-300">{event.Part || event.part || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {!isEditing && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleEdit(event)}
                              className="p-2 text-blue-400 hover:bg-blue-900/20 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(eventId)}
                              disabled={saving}
                              className="p-2 text-red-400 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                              title="Eliminar"
                            >
                              {saving ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          
          {/* Controles de paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900/50 border-t border-gray-700">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-300">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              </div>
              <div className="text-sm text-gray-400">
                Mostrando {startIndex + 1}-{Math.min(endIndex, filteredAndSortedEvents.length)} de {filteredAndSortedEvents.length}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CommitEvents


